/**
 * Partner offers (REQ-025): what the traveller sees of an insurer's offer,
 * and the click-through to the insurer's own site.
 *
 * The quiet client: offers are never essential to the page they appear on,
 * so a failure here leaves the section out rather than raising a toast.
 */
import apiClient from "./apiClient";

export type OfferLanguage = "en" | "de" | "fr" | "es" | "pt";

export interface PartnerOffer {
  id: string;
  partnerName: string;
  productName: string;
  category: "travel_health" | "repatriation" | "cancellation" | "other";
  description: Partial<Record<OfferLanguage, string>>;
  /** Which disclosure line to show (AC-005). */
  disclosure: "per_contract" | "per_visit";
}

export const partnerOffersQueryKey = (tripId: string) => [
  "marketplace",
  "offers",
  tripId,
];

export const getPartnerOffers = async (
  tripId: string,
): Promise<PartnerOffer[]> => {
  const response = await apiClient.get(
    `/marketplace/offers?tripId=${encodeURIComponent(tripId)}`,
  );
  return response.data.data.offers;
};

/** Records the click and returns the insurer's link to open. */
export const clickPartnerOffer = async (
  offerId: string,
  tripId: string,
): Promise<string> => {
  const response = await apiClient.post(
    `/marketplace/offers/${encodeURIComponent(offerId)}/click`,
    { tripId },
  );
  return response.data.data.url;
};

/** The traveller's own "hide partner offers" setting (AC-008). */
export const getMarketplaceHidden = async (): Promise<boolean> => {
  const response = await apiClient.get("/users/me");
  return response.data.data.user?.marketplaceHidden === true;
};

export const setMarketplaceHidden = async (hidden: boolean): Promise<void> => {
  await apiClient.put("/users/me", { marketplaceHidden: hidden });
};

/** The description in the reader's language, else English, else any. */
export function offerDescription(
  offer: Pick<PartnerOffer, "description">,
  language: string,
): string {
  const map = offer.description;
  return (
    map[language as OfferLanguage] ??
    map.en ??
    Object.values(map).find(Boolean) ??
    ""
  );
}
